import { db } from '../firebase';
import firebase from 'firebase/app';

export const HADITHS_COLLECTION = 'ihadis_hadiths';
export const BOOKS_COLLECTION = 'ihadis_books';
export const CHAPTERS_COLLECTION = 'ihadis_chapters';
export const SYNC_META_COLLECTION = 'ihadis_sync_meta';

// Helper for delaying between batches
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get Paginated Hadiths from Firestore (with search, category, book, and filter)
 */
export async function getHadiths({ page = 1, filter, category, book, search = '', pageSize = 20 } = {}) {
  try {
    const snapshot = await db.collection(HADITHS_COLLECTION).get();
    let items = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((item) => !item.isDeleted);

    // Filter by publication status
    if (filter && filter !== 'all') {
      items = items.filter((d) => !!d.isPublished === (filter === 'published'));
    }
    // Filter by book/slug
    if (book && book !== 'all') {
      items = items.filter((d) => d.bookSlug === book || d.categoryId === book || d.source === book);
    }
    // Filter by category
    if (category && category !== 'all') {
      items = items.filter((d) => d.categoryId === category || d.chapterId === category);
    }
    // Search query
    if (search && search.trim() !== '') {
      const q = search.toLowerCase().trim();
      items = items.filter((d) => {
        const textBn = (d.translationBn || d.bengali || d.titleBn || '').toLowerCase();
        const textEn = (d.translationEn || d.english || d.titleEn || '').toLowerCase();
        const ref = String(d.hadithNumber || d.referenceNumber || '');
        return textBn.includes(q) || textEn.includes(q) || ref.includes(q);
      });
    }

    items.sort((a, b) => (a.order || a.orderIndex || 0) - (b.order || b.orderIndex || 0));

    const total = items.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const validPage = Math.max(1, Math.min(page, totalPages));
    const startIndex = (validPage - 1) * pageSize;
    const paginatedItems = items.slice(startIndex, startIndex + pageSize);

    return {
      items: paginatedItems,
      total,
      totalPages,
      page: validPage,
      pageSize,
      hasMore: startIndex + pageSize < total,
    };
  } catch (error) {
    console.error('Error fetching hadiths from Firestore:', error);
    return { items: [], total: 0, totalPages: 1, page: 1, pageSize, hasMore: false };
  }
}

export async function getHadithById(id) {
  const doc = await db.collection(HADITHS_COLLECTION).doc(id).get();
  if (!doc.exists) return null;
  return { id: doc.id, ...doc.data() };
}

export async function createHadith(data) {
  const maxOrder = await db.collection(HADITHS_COLLECTION).orderBy('orderIndex', 'desc').limit(1).get();
  const orderIndex = maxOrder.empty ? 0 : (maxOrder.docs[0].data().orderIndex || 0) + 1;
  
  const titleEn = data.title?.en || data.titleEn || '';
  const titleBn = data.title?.bn || data.titleBn || '';
  const transEn = data.translation?.en || data.translationEn || data.english || '';
  const transBn = data.translation?.bn || data.translationBn || data.bengali || '';
  const expEn = data.explanation?.en || data.explanationEn || '';
  const expBn = data.explanation?.bn || data.explanationBn || '';
  const arabicText = data.textArabic || data.arabic || '';
  const hadithNum = data.hadithNumber ? Number(data.hadithNumber) : (Number(data.referenceNumber) || orderIndex + 1);

  const docData = {
    ...data,
    orderIndex: data.orderIndex !== undefined ? data.orderIndex : orderIndex,
    order: data.order !== undefined ? data.order : orderIndex,
    // Unified text properties
    arabic: arabicText,
    textArabic: arabicText,
    title: { bn: titleBn, en: titleEn },
    titleBn,
    titleEn,
    translation: { bn: transBn, en: transEn },
    translationBn: transBn,
    translationEn: transEn,
    bengali: transBn,
    english: transEn,
    explanation: { bn: expBn, en: expEn },
    explanationBn: expBn,
    explanationEn: expEn,
    narrator: data.narrator || '',
    grade: data.grade || 'সহিহ হাদিস',
    gradeColor: data.gradeColor || '#10b981',
    chapter: data.chapter || '',
    chapterId: data.chapterId !== undefined ? Number(data.chapterId) : 1,
    subCategoryId: data.subCategoryId || `${data.bookSlug || data.source || 'book'}_ch_${data.chapterId || 1}`,
    categoryId: data.categoryId || data.bookSlug || data.source || 'bukhari',
    bookSlug: data.bookSlug || data.source || data.categoryId || 'bukhari',
    bookId: data.bookId || data.bookSlug || data.source || 'bukhari',
    hadithNumber: hadithNum,
    referenceNumber: String(data.referenceNumber || hadithNum),
    reference: data.reference || `${data.source || data.bookSlug || 'Hadith'} ${hadithNum}`,
    teachings: Array.isArray(data.teachings) ? data.teachings : [],
    note: data.note || '',
    tags: Array.isArray(data.tags) ? data.tags : [],
    isPublished: data.isPublished !== undefined ? data.isPublished : true,
    isDeleted: false,
    sourceName: data.sourceName || 'iHadis',
    sourceWeb: data.sourceWeb || 'ihadis.com',
    sourceWebUrl: data.sourceWebUrl || 'https://ihadis.com',
    sourceUrl: data.sourceUrl || `https://ihadis.com/${data.bookSlug || 'hadith'}/${data.chapterId || 1}`,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    created_at: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    deleted_at: "",
  };

  if (data.id) {
    await db.collection(HADITHS_COLLECTION).doc(data.id).set(docData, { merge: true });
    return { id: data.id, ...docData };
  } else {
    const generatedId = `${docData.bookSlug}_${hadithNum}`;
    const existingDoc = await db.collection(HADITHS_COLLECTION).doc(generatedId).get();
    if (!existingDoc.exists) {
      await db.collection(HADITHS_COLLECTION).doc(generatedId).set(docData);
      return { id: generatedId, ...docData };
    }
    const addedRef = await db.collection(HADITHS_COLLECTION).add(docData);
    return { id: addedRef.id, ...docData };
  }
}

export async function updateHadith(id, data) {
  const titleEn = data.title?.en !== undefined ? data.title.en : data.titleEn;
  const titleBn = data.title?.bn !== undefined ? data.title.bn : data.titleBn;
  const transEn = data.translation?.en !== undefined ? data.translation.en : (data.translationEn || data.english);
  const transBn = data.translation?.bn !== undefined ? data.translation.bn : (data.translationBn || data.bengali);
  const expEn = data.explanation?.en !== undefined ? data.explanation.en : data.explanationEn;
  const expBn = data.explanation?.bn !== undefined ? data.explanation.bn : data.explanationBn;
  const arabicText = data.textArabic !== undefined ? data.textArabic : data.arabic;
  const hadithNum = data.hadithNumber ? Number(data.hadithNumber) : undefined;

  const updates = {
    ...data,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: firebase.firestore.FieldValue.serverTimestamp(),
  };

  if (arabicText !== undefined) {
    updates.arabic = arabicText;
    updates.textArabic = arabicText;
  }
  if (titleEn !== undefined || titleBn !== undefined) {
    updates.title = {
      bn: titleBn !== undefined ? titleBn : '',
      en: titleEn !== undefined ? titleEn : '',
    };
    if (titleEn !== undefined) updates.titleEn = titleEn;
    if (titleBn !== undefined) updates.titleBn = titleBn;
  }
  if (transEn !== undefined || transBn !== undefined) {
    updates.translation = {
      bn: transBn !== undefined ? transBn : '',
      en: transEn !== undefined ? transEn : '',
    };
    if (transEn !== undefined) {
      updates.translationEn = transEn;
      updates.english = transEn;
    }
    if (transBn !== undefined) {
      updates.translationBn = transBn;
      updates.bengali = transBn;
    }
  }
  if (expEn !== undefined || expBn !== undefined) {
    updates.explanation = {
      bn: expBn !== undefined ? expBn : '',
      en: expEn !== undefined ? expEn : '',
    };
    if (expEn !== undefined) updates.explanationEn = expEn;
    if (expBn !== undefined) updates.explanationBn = expBn;
  }
  if (hadithNum !== undefined) {
    updates.hadithNumber = hadithNum;
  }

  return db.collection(HADITHS_COLLECTION).doc(id).update(updates);
}

export async function deleteHadith(id) {
  return db.collection(HADITHS_COLLECTION).doc(id).update({
    isDeleted: true,
    deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    deleted_at: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function reorderHadith(id, newOrderIndex) {
  return db.collection(HADITHS_COLLECTION).doc(id).update({
    orderIndex: newOrderIndex,
    order: newOrderIndex,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function getHadithBooks() {
  try {
    const snapshot = await db.collection(BOOKS_COLLECTION).orderBy('orderIndex').get();
    if (!snapshot.empty) {
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    }
  } catch (err) {
    console.warn('Firestore books load fallback:', err);
  }

  // If empty in Firestore, return fallback from public/data
  try {
    const res = await fetch('/data/hadith/hadith_books.json');
    if (res.ok) return await res.json();
  } catch (e) {
    // fallback
  }
  return [];
}

export async function addHadithBook(id, nameEn, nameBn, extra = {}) {
  const maxOrder = await db.collection(BOOKS_COLLECTION).orderBy('orderIndex', 'desc').limit(1).get();
  const orderIndex = maxOrder.empty ? 0 : (maxOrder.docs[0].data().orderIndex || 0) + 1;
  const bookDoc = {
    id,
    slug: id,
    name: nameEn,
    nameEn,
    nameBn,
    title: nameEn,
    bengaliTitle: nameBn,
    author: extra.author || '',
    publication: extra.publication || '',
    orderIndex,
    order: orderIndex,
    sourceName: extra.sourceName || 'iHadis',
    sourceWeb: 'ihadis.com',
    sourceWebUrl: 'https://ihadis.com',
    sourceUrl: `https://ihadis.com/${id}`,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    ...extra,
  };
  await db.collection(BOOKS_COLLECTION).doc(id).set(bookDoc, { merge: true });
  return bookDoc;
}

export async function getHadithChapters(bookSlug) {
  try {
    let query = db.collection(CHAPTERS_COLLECTION);
    if (bookSlug && bookSlug !== 'all') {
      query = query.where('bookSlug', '==', bookSlug);
    }
    const snap = await query.orderBy('chapterId', 'asc').get();
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  } catch (err) {
    console.warn('Firestore getHadithChapters fallback:', err);
  }

  // Fallback to local JSON
  try {
    const res = await fetch('/data/hadith/hadith_chapters.json');
    if (res.ok) {
      const data = await res.json();
      if (bookSlug && data[bookSlug]) {
        return data[bookSlug];
      }
      if (!bookSlug || bookSlug === 'all') {
        const allList = [];
        Object.keys(data).forEach(k => {
          allList.push(...(data[k] || []));
        });
        return allList;
      }
    }
  } catch (e) {
    console.warn('Local chapters read failed:', e);
  }
  return [];
}

export async function addHadithChapter(data) {
  const chapterIdNum = Number(data.chapterId) || 1;
  const docId = data.id || `${data.bookSlug || 'book'}_ch_${chapterIdNum}`;
  const chapterObj = {
    id: docId,
    subCategoryId: docId,
    chapterId: chapterIdNum,
    categoryId: data.bookSlug,
    bookSlug: data.bookSlug,
    title: data.title || data.titleBn || '',
    titleBn: data.titleBn || data.title || '',
    titleEn: data.titleEn || data.title || '',
    order: chapterIdNum,
    orderIndex: chapterIdNum,
    sourceName: data.sourceName || 'iHadis',
    sourceWeb: 'ihadis.com',
    sourceWebUrl: 'https://ihadis.com',
    sourceUrl: data.sourceUrl || `https://ihadis.com/${data.bookSlug}/${chapterIdNum}`,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  await db.collection(CHAPTERS_COLLECTION).doc(docId).set(chapterObj, { merge: true });
  return chapterObj;
}

export async function getHadithCategories() {
  return getHadithBooks();
}

export async function addHadithCategory(id, nameEn, nameBn) {
  return addHadithBook(id, nameEn, nameBn);
}

export async function updateHadithBook(id, updates) {
  return db.collection(BOOKS_COLLECTION).doc(id).set({
    ...updates,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  }, { merge: true });
}

export async function updateHadithBookOrder(id, orderIndex) {
  return db.collection(BOOKS_COLLECTION).doc(id).set({
    orderIndex,
    order: orderIndex,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  }, { merge: true });
}

export async function deleteHadithBook(id) {
  return db.collection(BOOKS_COLLECTION).doc(id).delete();
}

export async function updateHadithCategory(id, updates) {
  return updateHadithBook(id, updates);
}

export async function updateHadithCategoryOrder(id, orderIndex) {
  return updateHadithBookOrder(id, orderIndex);
}

export async function deleteHadithCategory(id) {
  return deleteHadithBook(id);
}

export async function getTotalHadiths() {
  try {
    const snapshot = await db.collection(HADITHS_COLLECTION).get();
    return snapshot.size;
  } catch (e) {
    return 0;
  }
}

// -------------------------------------------------------------
// SAFE CLOUD UPLOADER / SYNC ENGINE
// -------------------------------------------------------------

/**
 * Fetch local JSON dataset from public/data/hadith/
 */
export async function loadLocalHadithData() {
  const [booksRes, chaptersRes, hadithsRes] = await Promise.all([
    fetch('/data/hadith/hadith_books.json'),
    fetch('/data/hadith/hadith_chapters.json'),
    fetch('/data/hadith/all_hadiths.json'),
  ]);

  if (!booksRes.ok || !chaptersRes.ok || !hadithsRes.ok) {
    throw new Error('Failed to load local Hadith JSON assets from public/data/hadith/');
  }

  const books = await booksRes.json();
  const chaptersMap = await chaptersRes.json();
  const hadiths = await hadithsRes.json();

  // Flatten chapters list
  const chaptersList = [];
  Object.keys(chaptersMap).forEach((slug) => {
    chaptersMap[slug].forEach((ch) => chaptersList.push(ch));
  });

  return { books, chaptersMap, chaptersList, hadiths };
}

/**
 * Get current sync statistics comparing local JSON vs Cloud Firestore
 */
export async function getSyncStats() {
  const [booksSnap, chaptersSnap, hadithsSnap, metaSnap] = await Promise.all([
    db.collection(BOOKS_COLLECTION).get(),
    db.collection(CHAPTERS_COLLECTION).get(),
    db.collection(HADITHS_COLLECTION).get(),
    db.collection(SYNC_META_COLLECTION).doc('status').get(),
  ]);

  let localData = { books: [], chaptersList: [], hadiths: [] };
  try {
    localData = await loadLocalHadithData();
  } catch (e) {
    console.warn('Could not read local data for stats:', e);
  }

  const meta = metaSnap.exists ? metaSnap.data() : null;

  return {
    local: {
      booksCount: localData.books.length,
      chaptersCount: localData.chaptersList.length,
      hadithsCount: localData.hadiths.length,
    },
    cloud: {
      booksCount: booksSnap.size,
      chaptersCount: chaptersSnap.size,
      hadithsCount: hadithsSnap.size,
    },
    meta,
  };
}

/**
 * Upload Books & Chapters metadata safely
 */
export async function uploadBooksAndChapters({ onProgress, onStatus, signal } = {}) {
  const { books, chaptersList } = await loadLocalHadithData();

  if (onStatus) onStatus(`Starting upload of ${books.length} Books and ${chaptersList.length} Chapters...`);

  // 1. Upload Books in 1 batch
  let batch = db.batch();
  books.forEach((b) => {
    const docRef = db.collection(BOOKS_COLLECTION).doc(b.id || b.slug);
    batch.set(docRef, {
      ...b,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    }, { merge: true });
  });
  await batch.commit();

  if (onStatus) onStatus(`Uploaded ${books.length} Books. Now uploading Chapters in safe batches...`);

  // 2. Upload Chapters in batches of 200
  const BATCH_SIZE = 200;
  for (let i = 0; i < chaptersList.length; i += BATCH_SIZE) {
    if (signal && signal.aborted) throw new Error('Upload cancelled by user.');

    const slice = chaptersList.slice(i, i + BATCH_SIZE);
    const chapterBatch = db.batch();

    slice.forEach((ch) => {
      const docRef = db.collection(CHAPTERS_COLLECTION).doc(ch.id);
      chapterBatch.set(docRef, {
        ...ch,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true });
    });

    await chapterBatch.commit();
    const progress = Math.min(100, Math.round(((i + slice.length) / chaptersList.length) * 100));
    if (onProgress) onProgress(progress);
    if (onStatus) onStatus(`Uploaded ${i + slice.length} / ${chaptersList.length} Chapters (${progress}%)...`);
    await sleep(150); // Safe delay
  }

  // Record metadata
  await db.collection(SYNC_META_COLLECTION).doc('status').set({
    booksCount: books.length,
    chaptersCount: chaptersList.length,
    metadataUploadedAt: firebase.firestore.FieldValue.serverTimestamp(),
  }, { merge: true });

  if (onStatus) onStatus('Books and Chapters successfully uploaded to Firebase!');
  return { booksCount: books.length, chaptersCount: chaptersList.length };
}

/**
 * Upload Hadiths in flexible batches (Part 1 / Half 50%, Part 2 / Remaining 50%, or All)
 * @param {Object} options
 * @param {number} options.startRatio - 0.0 for start
 * @param {number} options.endRatio - 0.5 for half, 1.0 for all
 * @param {Function} options.onProgress - (percentage, currentCount, totalCount)
 * @param {Function} options.onStatus - (statusMessage)
 * @param {Object} options.signal - AbortController signal
 */
export async function uploadHadithsBatch({
  startRatio = 0,
  endRatio = 1,
  onProgress,
  onStatus,
  signal,
} = {}) {
  const { hadiths } = await loadLocalHadithData();
  const total = hadiths.length;

  const startIndex = Math.max(0, Math.floor(total * startRatio));
  const endIndex = Math.min(total, Math.ceil(total * endRatio));
  const targetItems = hadiths.slice(startIndex, endIndex);

  if (onStatus) {
    onStatus(`Preparing batch: Items ${startIndex + 1} to ${endIndex} (Total ${targetItems.length} of ${total})...`);
  }

  const BATCH_SIZE = 150; // Well within Firestore limit of 500
  let uploadedCount = 0;

  for (let i = 0; i < targetItems.length; i += BATCH_SIZE) {
    if (signal && signal.aborted) {
      throw new Error('Upload paused/cancelled by user.');
    }

    const chunk = targetItems.slice(i, i + BATCH_SIZE);
    const batch = db.batch();

    chunk.forEach((h) => {
      const docRef = db.collection(HADITHS_COLLECTION).doc(h.id);
      batch.set(docRef, {
        ...h,
        isPublished: h.isPublished !== undefined ? h.isPublished : true,
        isDeleted: false,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true });
    });

    await batch.commit();
    uploadedCount += chunk.length;

    const currentGlobalIndex = startIndex + uploadedCount;
    const batchPercent = Math.min(100, Math.round((uploadedCount / targetItems.length) * 100));

    if (onProgress) {
      onProgress(batchPercent, uploadedCount, targetItems.length, currentGlobalIndex, total);
    }
    if (onStatus) {
      onStatus(`Uploaded ${uploadedCount} / ${targetItems.length} hadiths in this batch (${batchPercent}%)...`);
    }

    // Gentle 180ms delay between batches to stay comfortably within free tier write rate
    await sleep(180);
  }

  // Update Firestore sync metadata document
  const isHalf = endRatio <= 0.55;
  const isFull = endRatio >= 0.99 && startRatio <= 0.01;

  await db.collection(SYNC_META_COLLECTION).doc('status').set({
    totalHadithsInJson: total,
    lastUploadedIndex: endIndex,
    halfUploaded: isHalf || (endIndex >= Math.floor(total / 2)),
    completed: isFull || endIndex >= total,
    lastBatchDate: firebase.firestore.FieldValue.serverTimestamp(),
    lastBatchRange: `${startIndex + 1} - ${endIndex}`,
  }, { merge: true });

  if (onStatus) {
    onStatus(`Successfully uploaded ${uploadedCount} hadiths! Batch complete.`);
  }

  return { uploadedCount, startIndex, endIndex, total };
}